import { useEffect, useMemo } from "react";
import { useCollection } from "react-firebase-hooks/firestore";
import { db } from "@poseidon/firebase/firebase";
import { collection, query, where } from "firebase/firestore";
import {
  mapArray,
  pipe,
  reverseArray,
  sortArray,
} from "@poseidon/utils/pipe";
import { getDate } from "@poseidon/firebase/timestamp";

const mapTransaction = (doc) => {
  let data = doc.data();
  return {
    id: doc.id,
    ...data,
    timestamp: getDate(data.timestamp),
  };
};

const timestampSorter = (a, b) => a.timestamp.getTime() - b.timestamp.getTime();
const paymentNumberSorter = (a, b) => {
  if (
    !a.scheduleNumber ||
    !b.scheduleNumber ||
    !a.paymentNumber ||
    !b.paymentNumber
  ) {
    return 0;
  }
  return (
    a.scheduleNumber - b.scheduleNumber || a.paymentNumber - b.paymentNumber
  );
};
const interestBeforePaymentsSorter = (a, b) => {
  const an = a.amount < 0;
  const bn = b.amount < 0;
  if (an === bn) {
    return 0;
  }
  return an ? -1 : 1;
};

const transactionSorter = (a, b) => {
  return (
    timestampSorter(a, b) ||
    paymentNumberSorter(a, b) ||
    interestBeforePaymentsSorter(a, b)
  );
};

const calculateBalance = (docs) => {
  return docs.reduce((agg, trans, index, all) => {
    const previousTransaction = agg[index - 1] ?? {
      capitalBalance: 0,
      interestBalance: 0,
    };

    return [
      ...agg,
      {
        ...trans,
        capitalBalance:
          previousTransaction.capitalBalance + (trans.capitalChange ?? 0),
        interestBalance:
          previousTransaction.interestBalance + (trans.interestChange ?? 0),
      },
    ];
  }, []);
};
const pipeline = pipe(
  mapArray(mapTransaction),
  sortArray(transactionSorter),
  calculateBalance,
  reverseArray
);
export const useTransactions = (agreementId) => {
  const [value, loading, error] = useCollection(
    query(
      collection(db, "transactions"),
      where("agreementId", "==", agreementId)
    )
  );
  useEffect(() => {
    if (error) {
      console.error("Error reading transactions", error);
    }
  }, [error]);
  const transactions = useMemo(() => {
    if (!value) {
      return [];
    }
    return pipeline(value.docs);
  }, [value]);
  return { transactions, loading, error };
};
